import { gql } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { fromError } from '~utils/errors';
import {
  SyncScheduleDisplaysModal_ScheduleFragment,
  useSyncScheduleToDisplaysMutation,
} from './__generated__/SyncScheduleDisplaysModal.graphql';

interface Props {
  schedule: SyncScheduleDisplaysModal_ScheduleFragment;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: () => Promise<void> | void;
}

const schema = z.object({ scheduleId: z.string() });

type FormValues = z.infer<typeof schema>;

export function SyncScheduleDisplaysModal({ schedule, isOpen, onCancel, onSuccess }: Props) {
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<FormValues>({
    // Only scheduleId is a default value: the display ids to sync are resolved
    // server-side at submission time, and would otherwise go stale through polling.
    defaultValues: { scheduleId: schedule.id },
    resolver: zodResolver(schema),
  });

  const handleClose = useCallback(() => {
    reset();
    onCancel();
  }, [reset, onCancel]);

  const [syncDisplays] = useSyncScheduleToDisplaysMutation();
  const toast = useToast();

  const performSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await syncDisplays({ variables: { input: { scheduleId: values.scheduleId } } });

        toast({
          status: 'success',
          title: t('syncDisplayStart'),
          description: t('syncDisplayDesc'),
        });

        reset();
        await onSuccess?.();
      } catch (err) {
        toast({
          status: 'error',
          title: t('syncDisplayErr'),
          description: fromError(err, 'SyncToDisplays'),
        });
      }
    },
    [syncDisplays, toast, reset, onSuccess],
  );

  return (
    <Modal isOpen={isOpen} onClose={onCancel} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('syncDisplays')}</ModalHeader>
        <ModalCloseButton tabIndex={5} />
        <form onSubmit={handleSubmit(performSubmit)}>
          <ModalBody>
            <Text>{t('syncDisplaysWarn')}</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={handleClose}
              variant="ghost"
              colorScheme="blue"
              isDisabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="solid"
              colorScheme="blue"
              marginLeft="3"
              type="submit"
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
            >
              {t('scheduleSync')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

SyncScheduleDisplaysModal.graphql = {
  fragments: {
    SyncScheduleDisplaysModal_schedule: gql`
      fragment SyncScheduleDisplaysModal_schedule on Schedule {
        id
        outOfSyncDisplays: displays(filter: { state: OUT_OF_SYNC }) {
          id
        }
      }
    `,
  },
  mutations: {
    SyncScheduleToDisplays: gql`
      mutation SyncScheduleToDisplays($input: ScheduleSyncDisplaysInput!) {
        scheduleSyncDisplays(input: $input) {
          displays {
            id
          }
        }
      }
    `,
  },
};
